import {ReactNode, useMemo, useState} from 'react';
import {Alert, Modal, Spinner, Table} from '@goproblems/ui';
import {useLazyFetchScenarioNodeTagEventsQuery} from '@api';
import {
    FetchScenarioNodeTagEventsRequest,
    ScenarioNodeTag,
    ScenarioNodeTagEvent,
    ScenarioSolutionNodeStats,
} from '@types';
import {ScenarioEndnessTags} from './ScenarioEndnessTags';
import {useTheme} from './theme/useTheme';

type ScenarioNodeInfoProps = {
    stats?: ScenarioSolutionNodeStats | null;
    tags?: ScenarioNodeTag[] | null;
    scenarioId?: number | null;
    difficulty?: string | null;
    path?: string | null;
    className?: string;
    showEndness?: boolean;
    showTagHistory?: boolean;
};

const formatSignedNumber = (value: number) =>
    `${value > 0 ? '+' : ''}${value.toFixed(2)}`;

const numberClassName = (value: number) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-neutral-500';
};

const hasNumber = (value: number | null | undefined): value is number =>
    value !== null && value !== undefined;

const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));

export const ScenarioNodeInfo = ({
    stats,
    tags,
    scenarioId,
    difficulty,
    path,
    className = '',
    showEndness = true,
    showTagHistory = true,
}: ScenarioNodeInfoProps) => {
    const isDark = useTheme() === 'dark';
    const [showHistory, setShowHistory] = useState(false);
    const [
        fetchTagEvents,
        {data: tagEvents, isFetching: isFetchingTagEvents, isError},
    ] = useLazyFetchScenarioNodeTagEventsQuery();

    const displayTags = stats ? stats.tags : (tags ?? []);
    const endnessTags = showEndness
        ? displayTags.filter(tag => tag.type === 'endness')
        : [];
    const historyRequest =
        useMemo((): FetchScenarioNodeTagEventsRequest | null => {
            if (stats?.nodeId) {
                return {scenarioNodeId: stats.nodeId};
            }

            if (scenarioId && difficulty) {
                return {scenarioId, difficulty, path: path ?? ''};
            }

            return null;
        }, [difficulty, path, scenarioId, stats?.nodeId]);

    const hasInfo =
        hasNumber(stats?.score) ||
        hasNumber(stats?.scoreDelta) ||
        hasNumber(stats?.weight) ||
        endnessTags.length > 0;
    const canShowHistory = showTagHistory && historyRequest !== null;

    if (!hasInfo && !canShowHistory) {
        return null;
    }

    const handleShowHistory = () => {
        if (!historyRequest) return;

        setShowHistory(true);
        void fetchTagEvents(historyRequest);
    };

    return (
        <>
            <div
                className={`rounded p-3 ${isDark ? 'bg-neutral-800' : 'bg-neutral-50'} ${className}`}>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                    {hasNumber(stats?.score) && (
                        <InfoItem label="Score">
                            <span
                                className={`font-semibold ${numberClassName(stats.score)}`}>
                                {formatSignedNumber(stats.score)}
                            </span>
                        </InfoItem>
                    )}
                    {hasNumber(stats?.scoreDelta) && (
                        <InfoItem label="Score Delta">
                            <span
                                className={`font-semibold ${numberClassName(stats.scoreDelta)}`}>
                                {formatSignedNumber(stats.scoreDelta)}
                            </span>
                        </InfoItem>
                    )}
                    {hasNumber(stats?.weight) && (
                        <InfoItem label="Weight">
                            <span className="font-semibold text-neutral-500">
                                {stats.weight.toFixed(2)}
                            </span>
                        </InfoItem>
                    )}
                    {endnessTags.length > 0 && (
                        <InfoItem label="Endness" align="center">
                            <ScenarioEndnessTags tags={endnessTags} />
                        </InfoItem>
                    )}
                    {canShowHistory && (
                        <button
                            type="button"
                            className="ml-auto rounded border border-neutral-300 bg-transparent px-2 py-0.5 text-xs text-body-secondary"
                            onClick={handleShowHistory}>
                            Tag History
                        </button>
                    )}
                </div>
            </div>

            <ScenarioNodeTagHistoryModal
                show={showHistory}
                onHide={() => setShowHistory(false)}
                events={tagEvents ?? []}
                isLoading={isFetchingTagEvents}
                isError={isError}
            />
        </>
    );
};

const InfoItem = ({
    label,
    align = 'baseline',
    children,
}: {
    label: string;
    align?: 'baseline' | 'center';
    children: ReactNode;
}) => (
    <div
        className={`flex ${align === 'center' ? 'items-center' : 'items-baseline'} gap-1.5`}>
        <span className="text-body-secondary">{label}:</span>
        {children}
    </div>
);

const ScenarioNodeTagHistoryModal = ({
    show,
    onHide,
    events,
    isLoading,
    isError,
}: {
    show: boolean;
    onHide: () => void;
    events: ScenarioNodeTagEvent[];
    isLoading: boolean;
    isError: boolean;
}) => (
    <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
            <Modal.Title>Tag History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {isLoading ? (
                <div className="py-4 text-center">
                    <Spinner size="sm" className="me-2" />
                    Loading history...
                </div>
            ) : isError ? (
                <Alert variant="danger" className="mb-0">
                    Failed to load tag history.
                </Alert>
            ) : events.length === 0 ? (
                <div className="py-4 text-center text-muted">
                    No tag history.
                </div>
            ) : (
                <Table responsive size="sm" className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th style={{width: 170}}>Time</th>
                            <th style={{width: 90}}>Action</th>
                            <th>Tag</th>
                            <th style={{width: 260}}>Context</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map(event => (
                            <tr key={event.id}>
                                <td className="text-nowrap">
                                    {formatDateTime(event.createdAt)}
                                </td>
                                <td className="text-nowrap">
                                    <span
                                        className={`badge rounded ${event.action === 'added' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>
                                        {event.action}
                                    </span>
                                </td>
                                <td>
                                    <code>
                                        {event.tagType}.{event.tagCode}
                                    </code>
                                </td>
                                <td>
                                    {event.contextType ? (
                                        <span>
                                            {event.contextType}
                                            {event.contextId !== null
                                                ? ` #${event.contextId}`
                                                : ''}
                                        </span>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Modal.Body>
        <Modal.Footer>
            <button
                type="button"
                className="rounded border border-neutral-300 bg-transparent px-3 py-1 text-sm text-body-secondary"
                onClick={onHide}>
                Close
            </button>
        </Modal.Footer>
    </Modal>
);
